/**
 * Vendor Product API client — supplier SKUs (cr001_vendor_productses).
 *
 * Used by the reconciliation reference-data worklist to create a vendor product
 * for an unmatched import SKU. Mirrors the pure-fetch service pattern.
 */

import type {
  VendorProduct,
  VendorProductListResponse,
  CreateVendorProductRequest,
  UpdateVendorProductRequest,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

async function readError(response: Response, fallback: string): Promise<never> {
  const data = await response.json().catch(() => ({ error: `${fallback}: ${response.status}` }));
  throw new Error(data.error ?? `${fallback}: ${response.status}`);
}

export async function getVendorProducts(
  vendorId?: string,
  signal?: AbortSignal,
): Promise<VendorProduct[]> {
  const qs = vendorId ? `?vendorId=${encodeURIComponent(vendorId)}` : '';
  const response = await fetch(`${API_BASE_URL}/vendor-products${qs}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) await readError(response, 'Failed to fetch vendor products');
  const data: VendorProductListResponse = await response.json();
  return data.items ?? [];
}

export async function createVendorProduct(
  payload: CreateVendorProductRequest,
  signal?: AbortSignal,
): Promise<VendorProduct> {
  const response = await fetch(`${API_BASE_URL}/vendor-products`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) await readError(response, 'Failed to create vendor product');
  return (await response.json()) as VendorProduct;
}

export async function updateVendorProduct(
  id: string,
  payload: UpdateVendorProductRequest,
  signal?: AbortSignal,
): Promise<VendorProduct> {
  const response = await fetch(`${API_BASE_URL}/vendor-products/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) await readError(response, 'Failed to update vendor product');
  return (await response.json()) as VendorProduct;
}
